import { useMemo, useState, type FormEvent } from 'react'
import { REPO_RE, sizesInRegion } from '../../domain/founder'
import { money, TEMPLATE_LABEL } from '../../domain/format'
import type { CreateRunInput, Founder, TemplateName } from '../../domain/schemas'
import { teardownConfirmed } from '../../domain/stepActions'
import { ApiError } from '../../infra/api'
import { Button, Drawer, ErrorNote, Grid, Heading, Inline, Input, Segmented, Stack, Text } from '../../ui'
import { useApi } from '../api-context'
import { useLoad } from '../useLoad'
import { Field } from './ui'

const BLURB: Record<TemplateName, string> = {
  full: 'Box, repo from the starter, Railway app, custom domain and DNS. For a new founder.',
  adopt: 'Box, then attach a repo and a deploy target that already exist. Teardown never deletes what was attached.',
  boxOnly: 'Just the box. For a founder who builds before deciding where it runs.',
  addPerson: 'Lists the person, then their Linux user, Paseo, invite and pairing link (manual until the agent ships).',
  teardown: 'Deletes what cpg.computer created for this founder, by tag and recorded ids. Attached repos and projects stay.',
}

/**
 * Starts a run from the founder page, in the library Drawer: a side sheet on a desk, a full screen with Back on a
 * phone. Open while `template` is set. Its fields wait on the library's Field and Select (proposed on /__ui), so they
 * still use today's Field and one classed select.
 */
export function StartRunDialog({ founder, template, onClose, onStarted }: { founder: Founder; template: TemplateName | null; onClose: () => void; onStarted: (runId: string) => void }) {
  return (
    <Drawer id={template} onClose={onClose} title={(t) => `${TEMPLATE_LABEL[t as TemplateName]} run`}>
      {(t) => <RunForm key={t} founder={founder} template={t as TemplateName} onClose={onClose} onStarted={onStarted} />}
    </Drawer>
  )
}

const ROLES = [
  { value: 'founder', label: 'Founder' },
  { value: 'teammate', label: 'Teammate' },
  { value: 'viewer', label: 'Viewer' },
] as const

function RunForm({ founder, template, onClose, onStarted }: { founder: Founder; template: TemplateName; onClose: () => void; onStarted: (runId: string) => void }) {
  const { api } = useApi()
  const needsSize = template === 'full' || template === 'adopt' || template === 'boxOnly'
  const sizes = useLoad(() => (needsSize ? api.sizes() : Promise.resolve(null)), [api, needsSize])
  const offered = useMemo(() => (sizes.data ? sizesInRegion(sizes.data.sizes, founder.region) : []), [sizes.data, founder.region])
  // The founder's own size by default, even when this region lacks it: the box step places it (equivalent size or
  // nearest region) and records why.
  const [size, setSize] = useState<string>(founder.boxSize)

  const [repo, setRepo] = useState('')
  const [installationId, setInstallationId] = useState('')
  const [workspace, setWorkspace] = useState('')
  const [projectId, setProjectId] = useState('')
  const [serviceId, setServiceId] = useState('')
  const [environmentId, setEnvironmentId] = useState('')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'founder' | 'teammate' | 'viewer'>('teammate')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  const sizeOpt = size && size !== founder.boxSize ? { size } : {}
  const valid =
    template === 'adopt'
      ? REPO_RE.test(repo) && (installationId === '' || /^\d+$/.test(installationId)) && workspace.trim() !== '' && projectId.trim() !== '' && serviceId.trim() !== ''
      : template === 'addPerson'
        ? email.includes('@')
        : template === 'teardown'
          ? teardownConfirmed(confirm, founder.slug)
          : true

  function input(): CreateRunInput {
    const founderId = founder.id
    switch (template) {
      case 'full':
      case 'boxOnly':
        return { founderId, template, options: sizeOpt }
      case 'adopt':
        return {
          founderId,
          template,
          options: {
            ...sizeOpt,
            repo: repo.trim(),
            ...(installationId ? { installationId } : {}),
            deploy: { workspace: workspace.trim(), projectId: projectId.trim(), serviceId: serviceId.trim(), ...(environmentId.trim() ? { environmentId: environmentId.trim() } : {}) },
          },
        }
      case 'addPerson':
        return { founderId, template, options: { email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}), role } }
      case 'teardown':
        return { founderId, template, options: {} }
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!valid) return
    setBusy(true)
    setError(undefined)
    try {
      const run = await api.createRun(input())
      onStarted(run.id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack>
        <Text size="sm" tone="muted">{BLURB[template]}</Text>

        {needsSize && (
          <Field label="Box size" hint={`Sizes offered in ${founder.region}. Defaults to the founder's ${founder.boxSize}.`}>
            <select className="input" value={size} onChange={(e) => setSize(e.target.value)}>
              {!offered.some((s) => s.slug === founder.boxSize) && <option value={founder.boxSize}>{founder.boxSize} · not in {founder.region} now, placed nearby</option>}
              {offered.map((s) => (
                <option key={s.slug} value={s.slug} disabled={!s.selectable}>
                  {s.slug} · {s.memoryGb} GB · {money(s.priceMonthly)}
                  {s.selectable ? '' : ' · not offered yet'}
                </option>
              ))}
            </select>
          </Field>
        )}

        {template === 'adopt' && (
          <>
            <Grid cols={2}>
              <Field label="Repo" hint="owner/repo" error={repo && !REPO_RE.test(repo) ? 'owner/repo' : undefined}>
                <Input mono value={repo} onChange={(e) => setRepo(e.target.value)} required spellCheck={false} autoCapitalize="off" autoFocus />
              </Field>
              <Field label="Installation id (optional)" hint="The GitHub App installation that reaches the repo.">
                <Input mono value={installationId} onChange={(e) => setInstallationId(e.target.value)} inputMode="numeric" />
              </Field>
            </Grid>
            <Heading level={3}>Deploy target</Heading>
            <Grid cols={2}>
              <Field label="Workspace">
                <Input mono value={workspace} onChange={(e) => setWorkspace(e.target.value)} required spellCheck={false} />
              </Field>
              <Field label="Project id">
                <Input mono value={projectId} onChange={(e) => setProjectId(e.target.value)} required spellCheck={false} />
              </Field>
              <Field label="Service id">
                <Input mono value={serviceId} onChange={(e) => setServiceId(e.target.value)} required spellCheck={false} />
              </Field>
              <Field label="Environment id (optional)">
                <Input mono value={environmentId} onChange={(e) => setEnvironmentId(e.target.value)} spellCheck={false} />
              </Field>
            </Grid>
          </>
        )}

        {template === 'addPerson' && (
          <>
            <Grid cols={2}>
              <Field label="Email">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </Field>
              <Field label="Name (optional)">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
            </Grid>
            <Segmented label="Role" options={[...ROLES]} value={role} onChange={setRole} />
          </>
        )}

        {template === 'teardown' && (
          <Field label={`Type ${founder.slug} to confirm`}>
            <Input mono value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus spellCheck={false} autoCapitalize="off" autoComplete="off" />
          </Field>
        )}

        {error && <ErrorNote title="The run didn't start." reason={error} />}
        <Inline justify="end">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant={template === 'teardown' ? 'danger' : 'ink'} disabled={!valid || busy}>
            {busy ? 'Starting…' : template === 'teardown' ? `Tear down ${founder.slug}` : 'Start run'}
          </Button>
        </Inline>
      </Stack>
    </form>
  )
}
